import React from 'react';
import PropTypes from 'prop-types';
import { Editor } from 'slate-react';

import Toolbar from './Toolbar';
import * as EditorHelpers from '../helpers/Editor';

import 'PageStyles/lettersetup/editor.less';

const SCHEMA = {
  inlines: {
    image: {
      isVoid: true,
      data: {
        src: v => {
          if (!v) return false;
          if (v && EditorHelpers.isUrl(v)) return true;
          const type = v.split(';')[0].split('/')[1]; // get the type from the base64 data uri
          if (EditorHelpers.isImageType(type)) return true;
          return false;
        },
      },
    },
  },
};

class MPWEditor extends React.Component {
  static propTypes = {
    initialValue: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    name: PropTypes.string,
    tokens: PropTypes.arrayOf(
      PropTypes.shape({
        Token: PropTypes.string,
        Description: PropTypes.string,
      })
    ),
    initialPageSize: PropTypes.oneOfType([
      PropTypes.oneOf(['letter', 'legal']),
      PropTypes.shape([PropTypes.number, PropTypes.number]),
    ]),
    initialFontSize: PropTypes.number,
    initialFontFamily: PropTypes.string,
    initialMargins: PropTypes.shape({
      top: PropTypes.number,
      right: PropTypes.number,
      bottom: PropTypes.number,
      left: PropTypes.number,
    }),
    focusOnMount: PropTypes.bool,
  };

  static defaultProps = {
    name: '',
    token: null,
    tokens: [],
    initialPageSize: EditorHelpers.DEFAULT_PAGE_SIZE,
    initialFontSize: EditorHelpers.DEFAULT_FONT_SIZE,
    initialFontFamily: EditorHelpers.DEFAULT_FONT_FAMILY,
    initialMargins: {
      top: 1,
      left: 1,
      right: 1,
      bottom: 1,
    },
    focusOnMount: false,
    isDirty: false,
  };

  constructor(props) {
    super(props);
    this.editor = React.createRef();
    /**
     * initialValue can be Slate's Value, a string
     */
    const value = EditorHelpers.createValue(props.initialValue);

    this.state = {
      value,
      pageSize: props.initialPageSize,
      token: null,
      fontSize: props.initialFontSize,
      fontFamily: props.initialFontFamily,
      margins: props.initialMargins,
      hasUndo: false,
      hasRedo: false,
      isDirty: false,
    };
  }

  componentDidMount() {
    if (this.props.focusOnMount) {
      this.editor.change(change => {
        change.focus();
      });
    }
  }

  ref = editor => {
    this.editor = editor;
  };

  getCurrentValue = () => this.state.value;

  getCurrentHTML = () => EditorHelpers.html.serialize(this.getCurrentValue());

  onPageSize = pageSize => this.setState({ pageSize });

  onMargins = (whichMargin, marginSize) => {
    switch (whichMargin) {
      case 'all':
        this.setState({
          margins: {
            top: marginSize,
            right: marginSize,
            bottom: marginSize,
            left: marginSize,
          },
        });
        break;
      case 'left':
      case 'right':
      case 'bottom':
      case 'top':
        this.setState({
          margins: {
            ...this.state.margins,
            [whichMargin]: marginSize,
          },
        });
        break;
      default:
    }
  };

  onToken = token => this.setState({ token });

  onFontSize = fontSize => {
    const markType = 'font-size';
    this.setState({ fontSize }, () => {
      this.editor.change(change => {
        EditorHelpers.removeMarkFromSelection(change, markType);
        change
          .toggleMark({
            type: markType,
            data: {
              [markType]: fontSize,
            },
          })
          .focus();
      });
    });
  };

  onFontFamily = fontFamily => {
    const markType = 'font-family';
    this.setState({ fontFamily }, () => {
      this.editor.change(change => {
        EditorHelpers.removeMarkFromSelection(change, markType);
        change
          .toggleMark({
            type: markType,
            data: {
              [markType]: fontFamily,
            },
          })
          .focus();
      });
    });
  };

  onChange = ({ value }) => {
    const state = {
      value,
    };

    const { isDirty } = this.state;
    const { forceDirtyState, resetDirty } = this.props;

    const numUndos = value.data.get('undos').size;
    const numRedos = value.data.get('redos').size;
    state.hasUndo = numUndos > 2; // size of undo starts at 2
    state.hasRedo = numRedos > 0; // redo starts at 0

    if (numUndos === 2 && isDirty) {
      resetDirty();
      this.setState({ isDirty: false });
    }

    if (numUndos > 2 && !isDirty) {
      forceDirtyState();
      this.setState({ isDirty: true });
    }

    this.setState(state);
  };

  onImageUrl = src => {
    if (EditorHelpers.isUrl(src)) {
      EditorHelpers.getImageDimensionsFromUrl(src).then(dimensions => {
        this.editor.change(change => {
          const height = EditorHelpers.DEFAULT_IMAGE_HEIGHT;
          const width = (height / dimensions.height) * dimensions.width;
          EditorHelpers.insertImage(change, {
            src,
            width,
            height,
          });
        });
      });
    }
  };

  onImageFiles = files => {
    for (const file of files) {
      const reader = new FileReader();
      const [mime] = file.type.split('/');
      if (mime !== 'image') return;

      reader.addEventListener('load', () => {
        EditorHelpers.getImageDimensionsFromUrl(reader.result).then(
          dimensions => {
            this.editor.change(change => {
              const height = EditorHelpers.DEFAULT_IMAGE_HEIGHT;
              const width = (height / dimensions.height) * dimensions.width;
              EditorHelpers.insertImage(change, {
                src: reader.result,
                width,
                height,
              });
            });
          }
        );
      });
      reader.readAsDataURL(file);
    }
  };

  onInsert = () => {
    const { token } = this.state;
    if (typeof token === 'string') {
      this.editor.change(change => {
        change
          .insertText(token)
          .moveToStartOfNextText()
          .focus();
      });
    }
  };

  onMark = type => {
    this.editor.change(change => {
      change.toggleMark(type).focus();
    });
  };

  onAlign = alignment => {
    this.editor.change(change => {
      change.setBlocks({
        type: 'alignment',
        data: { alignment },
      });
    });
  };

  onUndo = () => {
    this.editor.change(change => {
      const undos = change.value.data.get('undos');
      if (undos.size > 0) {
        change.undo();
      }
    });
  };

  onRedo = () => {
    this.editor.change(change => {
      const redos = change.value.data.get('redos');
      if (redos.size > 0) {
        change.redo();
      }
    });
  };

  onSelect = (event, change, next) => {
    const { initialFontFamily, initialFontSize } = this.props;
    next();
    const { value } = change;
    let fontSizeAtSelection = initialFontSize;
    let fontFamilyAtSelection = initialFontFamily;

    const fontSizeMark = EditorHelpers.getFirstMark(value, 'font-size');
    const fontFamilyMark = EditorHelpers.getFirstMark(value, 'font-family');

    if (fontSizeMark) {
      fontSizeAtSelection = fontSizeMark.get('data').get('font-size');
    }

    if (fontFamilyMark) {
      fontFamilyAtSelection = fontFamilyMark.get('data').get('font-family');
    }
    /**
     * setTimeout required; the editor would not focus in time if we did a rerender during the middleware chain execution
     */
    setTimeout(() => {
      this.setState({
        fontSize: fontSizeAtSelection,
        fontFamily: fontFamilyAtSelection,
      });
    }, 50);
  };

  render() {
    const {
      fontSize,
      fontFamily,
      pageSize,
      margins,
      token,
      value,
      hasUndo,
      hasRedo,
    } = this.state;
    const { tokens, name } = this.props;
    const dimensions = EditorHelpers.getDimensions(pageSize, margins);
    const editorContainerStyle = {
      paddingTop: dimensions.paddingTop,
      paddingRight: dimensions.paddingRight,
      paddingBottom: dimensions.paddingBottom,
      paddingLeft: dimensions.paddingLeft,
      minHeight: dimensions.height, // set minHeight instead of height to support multiple pages
      width: dimensions.width,
    };
    return (
      <div className="RichEditor-root">
        <div className="RichEditor-toolbar">
          <Toolbar
            editor={this.editor}
            value={value}
            name={name}
            pageSize={pageSize}
            pageSizes={EditorHelpers.SUPPORTED_PAGE_SIZES}
            tokens={tokens}
            token={token}
            marginSizes={EditorHelpers.SUPPORTED_MARGINS}
            margins={margins}
            fontSizes={EditorHelpers.SUPPORTED_FONT_SIZES}
            fontSize={fontSize}
            fontFamilies={EditorHelpers.SUPPORTED_FONT_FAMILIES}
            fontFamily={fontFamily}
            onMark={this.onMark}
            onImageUrl={this.onImageUrl}
            onImageFiles={this.onImageFiles}
            onInsert={this.onInsert}
            onAlign={this.onAlign}
            onFontSize={this.onFontSize}
            onFontFamily={this.onFontFamily}
            onMargins={this.onMargins}
            onPageSize={this.onPageSize}
            onToken={this.onToken}
            onRedo={this.onRedo}
            onUndo={this.onUndo}
            hasUndo={hasUndo}
            hasRedo={hasRedo}
          />
        </div>
        <div className="RichEditor-editor" style={editorContainerStyle}>
          <Editor
            ref={this.ref}
            schema={SCHEMA}
            spellCheck
            value={value}
            onChange={this.onChange}
            onKeyDown={EditorHelpers.onKeyDown}
            renderNode={EditorHelpers.renderNode}
            renderMark={EditorHelpers.renderMark}
            onDrop={EditorHelpers.onDropOrPaste}
            onPaste={EditorHelpers.onDropOrPaste}
            onSelect={this.onSelect}
          />
        </div>
      </div>
    );
  }
}

export default MPWEditor;



// WEBPACK FOOTER //
// ./src/pages/lettersetup/components/MPWEditor.js